'use client';

import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import useClosedSearchEvent from 'hooks/useClosedSearchEvent';
import { getDateSearch, updateUrlQueryParam } from 'utils/misc';
import { ClosedSearchSubHeader } from 'components/ClosedSearchSubHeader';
import SearchEventList from 'components/SearchEventList/SearchEventList';
import {
  Container,
  SearchBodyContainer,
} from 'components/Container/Container.css';
import SearchContent from 'components/Shared/SearchContent/SearchContent';
import { Loading } from 'components/Loading';
import { defaultSearchTitle, urlParamsSearch } from 'constants/search';
import { listPredefinedDates } from 'constants/listPredefinedDates';
import { SearchEventResult } from 'searchEvent';
import { ParamsMapSearch, ParamsResult } from 'ParamsResult.d';
import { Location } from 'location';
import { InstantSearch } from 'react-instantsearch';
import { useFeature } from '@growthbook/growthbook-react';
import searchClient from 'utils/algolia';
import { addEventToDataLayer } from 'utils/addEventToDataLayer';
import { ALGOLIA_INDEX_NAME } from 'constants/algolia';
import { AlgoliaSearchEvents } from './AlgoliaSearchEvents/AlgoliaSearchEvents';

export type ClosedSearchEventTemplateProps = {
  params: ParamsResult;
  searchDataResult: SearchEventResult;
  isMobile: boolean;
  paramsMapSearch?: ParamsMapSearch;
  location: Location;
  slugs: string;
};

function ClosedSearchEventTemplate({
  searchDataResult,
  params,
  isMobile,
  paramsMapSearch,
  location,
  slugs,
}: ClosedSearchEventTemplateProps) {
  const title = params?.url_params?.component?.name
    || params?.search_params?.component_uuid?.toUpperCase() /** belo-horizonte-mg/hoje */
    || params?.url_params?.date?.name /** gastronomico/hoje */
    || defaultSearchTitle; /** empty */

  const isAlgoliaSearch = !slugs || listPredefinedDates.some((option) => slugs?.includes(option.dateId));
  const isAlgoliaGrowthbookEnabled = useFeature('show-algolia-search-page.discovery');
  const [showAlgoliaSearch, setShowAlgoliaSearch] = useState(isAlgoliaGrowthbookEnabled?.value && isAlgoliaSearch);

  // TODO: REFACTOR AFTER THE PREDEFINED SEARCH IS 100% ALGOLIA
  const closedSearchEventStore = useClosedSearchEvent();
  if (!showAlgoliaSearch) closedSearchEventStore.hydrate(searchDataResult);
  useEffect(() => {
    if (!showAlgoliaSearch) closedSearchEventStore.setResults(searchDataResult);
  }, [searchDataResult, paramsMapSearch]);

  useEffect(() => {
    if (window && isAlgoliaSearch) {
      const searchParams = new URLSearchParams(window.location.search);
      const showAlgoliaSearchFromUrl = searchParams.get('algolia') === 'true';
      if (showAlgoliaSearch === false && showAlgoliaSearchFromUrl) {
        setShowAlgoliaSearch(showAlgoliaSearchFromUrl);
      }
      addEventToDataLayer({
        stckEventCategory: showAlgoliaSearch || showAlgoliaSearchFromUrl ? 'searchPredefinedPageB' : 'searchPredefinedPageB',
        stckEventLabel: showAlgoliaSearch || showAlgoliaSearchFromUrl ? 'algolia' : 'default',
        stckEventAction: 'view',
        event: 'stck',
      });
    }
  }, []);

  if (showAlgoliaSearch) {
    const textDate = slugs?.split('/').pop() || '';
    const date = getDateSearch(textDate);
    return (
      <InstantSearch searchClient={searchClient} indexName={ALGOLIA_INDEX_NAME}>
        <AlgoliaSearchEvents
          params={params}
          location={location}
          date={date}
          title={title}
        />
      </InstantSearch>
    );
  }

  if (isAlgoliaSearch && isAlgoliaGrowthbookEnabled?.value === null) return (<Loading isNotFullScreen />);

  if (paramsMapSearch) closedSearchEventStore.hydrateParams(paramsMapSearch);
  return (
    <div>
      <ClosedSearchSubHeader params={params} title={title} />
      <div className={SearchBodyContainer}>
        <div className={Container}>
          <SearchContent
            pageTypeText="eventos"
            searchResult={closedSearchEventStore?.searchDataResult}
            totalPages={closedSearchEventStore?.totalPages}
            isFetching={closedSearchEventStore?.isFetching}
            isMobile={isMobile}
            onChangePage={(page: string | number) => {
              if (!isMobile) {
                updateUrlQueryParam({ param: urlParamsSearch.page, value: String(page) });
              }

              closedSearchEventStore.fetchPaginationData({
                query: window?.location?.search,
                isMobile,
                page,
                onSuccess: () => window?.scrollTo({ top: 0, left: 0 }),
              });
              return +page;
            }}
          >
            <SearchEventList events={closedSearchEventStore?.searchDataResult?.data} serviceParams={params} isMobile={isMobile} />
          </SearchContent>
        </div>
      </div>
    </div>
  );
}

export default observer(ClosedSearchEventTemplate);
